import { useEffect, useRef, useState } from "react";

/**
 * One stack of dismissable layers for the whole app: modal dialogs, overlay drawers, popovers and floating pickers.
 * Ruling 2026-10-01: a click OUTSIDE a dialog or drawer closes it, and Esc follows the same rules.
 *
 * - Only the innermost (top) layer reacts. A popover inside a dialog closes on the first outside click or Esc; the
 *   dialog stays open until the next one.
 * - An outside click needs BOTH the press and the release outside the layer, so a text selection dragged from inside
 *   to the backdrop never closes anything.
 * - A combobox or @mention list that is open inside the top layer is a layer of its own: the first outside click or
 *   Esc closes only that list (its owner already handles Esc; an outside click sends it an Escape).
 * - Whether a layer asks "Discard your changes?" first is the layer's own business (Dialog, DrawerLayout).
 * - A click on an app-level floating card that sits above every page and belongs to no layer (marked
 *   `data-dismiss-ignore`, e.g. the "what's new" prompt) is not an outside click: answering it never closes an open
 *   drawer or dialog underneath (O-R10-2).
 */
export type DismissReason = "outside" | "escape";

export interface DismissLayer {
  /** The layer's root element, used to order nested layers that mount in the same commit. */
  element: () => Element | null;
  /** True when this pointer event happened outside the layer. */
  isOutside: (event: MouseEvent) => boolean;
  dismiss: (reason: DismissReason) => void;
}

const stack: DismissLayer[] = [];
let pressed: { layer: DismissLayer; outside: boolean; innerPopup: HTMLElement | null } | null = null;
let escapeKeyThisTask = false;
let escapeInnerPopup = false;
let escapeFromRichEditor = false;
let synthesizing = false;
let installed = false;

function top(): DismissLayer | undefined {
  return stack[stack.length - 1];
}

/** The focused control inside `layer` whose own list is open (a combobox, or the editor's @mention list). */
function openInnerPopup(layer: DismissLayer | undefined): HTMLElement | null {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !active.matches('[aria-expanded="true"]:is([role="combobox"], [aria-haspopup="listbox"])')) return null;
  const root = layer?.element();
  return root && !root.contains(active) ? null : active;
}

/** Closes an inner list the way its owner already knows: an Escape on the control that owns it. */
function closeInnerPopup(control: HTMLElement): void {
  synthesizing = true;
  try {
    control.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", code: "Escape", bubbles: true, cancelable: true }));
  } finally {
    synthesizing = false;
  }
}

function onPointerDown(event: PointerEvent): void {
  const layer = top();
  if (!layer || event.button !== 0 || (event.target instanceof Element && event.target.closest("[data-dismiss-ignore]") !== null)) {
    pressed = null;
    return;
  }
  pressed = { layer, outside: layer.isOutside(event), innerPopup: openInnerPopup(layer) };
}

function onClick(event: MouseEvent): void {
  const press = pressed;
  pressed = null;
  const layer = top();
  // A keyboard "click" has no pointerdown; a drag that started inside has outside=false.
  if (!press || !layer || press.layer !== layer || !press.outside || !layer.isOutside(event)) return;
  if (press.innerPopup) {
    if (press.innerPopup.isConnected && press.innerPopup.getAttribute("aria-expanded") === "true") closeInnerPopup(press.innerPopup);
    return;
  }
  layer.dismiss("outside");
}

function onKeyDownCapture(event: KeyboardEvent): void {
  if (event.key !== "Escape" || synthesizing) return;
  // Noted before any handler runs: an owner that closes its own list on Esc must not also close the dialog.
  escapeKeyThisTask = true;
  escapeInnerPopup = openInnerPopup(top()) !== null;
  // O-R10-1: ProseMirror (the rich-text editor) cancels EVERY Esc it receives, handled or not, so a cancelled Esc from
  // inside it does not mean "already handled". Its real uses of Esc (the @mention list) are the inner-popup case above.
  escapeFromRichEditor = event.target instanceof Element && event.target.closest(".ProseMirror") !== null;
  setTimeout(() => {
    escapeKeyThisTask = false;
  }, 0);
}

function onKeyDown(event: KeyboardEvent): void {
  if (event.key !== "Escape" || synthesizing || (event.defaultPrevented && !(escapeFromRichEditor && !escapeInnerPopup))) return;
  if (escapeInnerPopup) {
    // Its owner closed the list; cancelling the key also stops a native <dialog> from receiving `cancel` for it.
    event.preventDefault();
    return;
  }
  const layer = top();
  if (!layer) return;
  event.preventDefault();
  layer.dismiss("escape");
}

function install(): void {
  if (installed) return;
  installed = true;
  document.addEventListener("pointerdown", onPointerDown, true);
  document.addEventListener("click", onClick, true);
  document.addEventListener("keydown", onKeyDownCapture, true);
  document.addEventListener("keydown", onKeyDown);
}

function uninstall(): void {
  if (!installed || stack.length > 0) return;
  installed = false;
  pressed = null;
  document.removeEventListener("pointerdown", onPointerDown, true);
  document.removeEventListener("click", onClick, true);
  document.removeEventListener("keydown", onKeyDownCapture, true);
  document.removeEventListener("keydown", onKeyDown);
}

/** Adds a layer above every layer it is not nested inside; returns the removal. */
export function pushDismissLayer(layer: DismissLayer): () => void {
  const element = layer.element();
  // A parent and its child can register in the same commit (child effects run first): keep the DOM ancestor below.
  const firstNested = element ? stack.findIndex((other) => {
    const inner = other.element();
    return inner !== null && inner !== element && element.contains(inner);
  }) : -1;
  if (firstNested >= 0) stack.splice(firstNested, 0, layer);
  else stack.push(layer);
  install();
  return () => {
    const index = stack.indexOf(layer);
    if (index >= 0) stack.splice(index, 1);
    if (pressed?.layer === layer) pressed = null;
    uninstall();
  };
}

/**
 * Batch 19 GRF-60: true while any layer is open (a dialog, an overlay drawer, a popover or picker). A docked drawer is not a
 * layer (the list beside it stays usable), so it closes on Esc only when nothing else would take that key.
 */
export function anyDismissLayerOpen(): boolean {
  return stack.length > 0;
}

export function isTopDismissLayer(layer: DismissLayer): boolean {
  return top() === layer;
}

/**
 * True while an Esc key press is being processed. A native <dialog> also receives `cancel` for that same Esc; the
 * stack has already routed the key to the innermost layer, so Dialog ignores that `cancel` (it still honours a
 * `cancel` with no Esc behind it, e.g. the Android back gesture).
 */
export function escapeKeyInProgress(): boolean {
  return escapeKeyThisTask;
}

/** Default outside test: the event target is not inside any of the given elements. */
export function outsideOf(event: MouseEvent, ...elements: (Element | null | undefined)[]): boolean {
  const target = event.target;
  if (!(target instanceof Node)) return false;
  return !elements.some((element) => element?.contains(target));
}

/** Registers a layer while `active`; the callbacks are read at event time, so they may change on every render. */
export function useDismissLayer(active: boolean, layer: DismissLayer): DismissLayer {
  const latest = useRef(layer);
  useEffect(() => {
    latest.current = layer;
  });
  const [stable] = useState<DismissLayer>(() => ({
    element: () => latest.current.element(),
    isOutside: (event) => latest.current.isOutside(event),
    dismiss: (reason) => latest.current.dismiss(reason),
  }));
  useEffect(() => {
    if (!active) return undefined;
    return pushDismissLayer(stable);
  }, [active, stable]);
  return stable;
}
